import React, { useState, useEffect } from 'react';
import api from '../api';
import CollapsibleFilters from './CollapsibleFilters';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import {
  FormField,
  FormGrid,
  FormSection,
  FormActions,
  StandardSelect,
  StandardTextarea,
  QuantityInput,
  DifferenceDisplay,
  ReasonSelector,
  ItemList,
  ValidationSummary,
  EnhancedFormField,
  useFormValidation,
  ADJUSTMENT_REASONS
} from './FormComponents';

interface StockAdjustment {
  id: number;
  adjustment_number: string;
  warehouse_id: number;
  adjustment_date: string;
  reason: string;
  status: string;
  notes?: string;
  warehouse?: {
    id: number;
    name: string;
  };
  items: AdjustmentItem[];
}

interface AdjustmentItem {
  product_id: number;
  current_quantity: number;
  adjusted_quantity: number;
  difference: number;
  notes?: string;
}

interface Warehouse {
  id: number;
  code: string;
  name: string;
}

interface Product {
  id: number;
  sku: string;
  name: string;
  attributes?: Array<{ attribute_name: string; value: string }>;
}

// Helper: format product label as "SKU - Name - Color - Decoration"
const getProductLabel = (product: Product) => {
  const color = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value || '';
  const decoration = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value || '';
  let label = `${product.sku} - ${product.name}`;
  if (color) label += ` - ${color}`;
  if (decoration) label += ` - ${decoration}`;
  return label;
};

const StockAdjustmentManager: React.FC = () => {
  const [adjustments, setAdjustments] = useState<StockAdjustment[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Bulk import state
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // Bulk status update state
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [bulkStatusTarget, setBulkStatusTarget] = useState('');
  const [bulkStatusLoading, setBulkStatusLoading] = useState(false);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchText]);
  
  const [formData, setFormData] = useState({
    warehouse_id: 0,
    reason: 'physical_count',
    notes: '',
    items: [] as AdjustmentItem[]
  });

  // Enhanced form validation
  const {
    values: validationValues,
    errors: validationErrors,
    touched: validationTouched,
    setValue: setValidationValue,
    setFieldTouched,
    validateForm,
    resetForm: resetValidation
  } = useFormValidation(
    {
      warehouse_id: 0,
      reason: '',
      items: []
    },
    {
      warehouse_id: {
        required: true,
        custom: (value) => value === 0 ? 'Please select a warehouse' : null
      },
      reason: {
        required: true,
        custom: (value) => !value ? 'Please select a reason for adjustment' : null
      },
      items: {
        required: true,
        custom: (value) => {
          if (!Array.isArray(value) || value.length === 0) {
            return 'At least one item is required';
          }
          
          const errors: string[] = [];
          const productIds = new Set();
          
          value.forEach((item, index) => {
            if (!item.product_id) {
              errors.push(`Item ${index + 1}: Please select a product`);
            } else if (productIds.has(item.product_id)) {
              errors.push(`Item ${index + 1}: Duplicate product not allowed`);
            } else {
              productIds.add(item.product_id);
            }
            
            if (item.adjusted_quantity < 0) {
              errors.push(`Item ${index + 1}: Adjusted quantity cannot be negative`);
            }
          });
          
          return errors.length > 0 ? errors.join('; ') : null;
        }
      }
    }
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, debouncedSearch, dateFrom, dateTo]);

  const fetchData = async () => {
    try {
      const skip = (currentPage - 1) * pageSize;
      const params: any = { skip, limit: pageSize };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [adjustmentsRes, warehousesRes, productsRes] = await Promise.all([
        api.get('/inventory/adjustments/', { params }),
        api.get('/inventory/warehouses/'),
        api.get('/products/', { params: { limit: 500 } })
      ]);
      setAdjustments(adjustmentsRes.data);
      setTotalCount(adjustmentsRes.data.length);
      setWarehouses(warehousesRes.data);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search is now server-side, no client-side filtering needed

  const handleDelete = async (id: number) => {
    const adj = adjustments.find(a => a.id === id);
    setDeleteConfirm({show: true, id, name: adj?.adjustment_number || `Adjustment #${id}`});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/inventory/adjustments/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to delete adjustment');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? adjustments.map(a => a.id) : []);
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    setSelectedIds(checked ? [...selectedIds, id] : selectedIds.filter(i => i !== id));
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.length > 1) setShowBulkDeleteConfirm(true);
    else if (selectedIds.length === 1) { handleDelete(selectedIds[0]); setSelectedIds([]); }
  };

  const confirmBulkDelete = async () => {
    if (confirmText !== 'confirm') return;
    const errors: string[] = [];
    for (const id of selectedIds) {
      try { await api.delete(`/inventory/adjustments/${id}`); }
      catch (error: any) { errors.push(`Adjustment ${id}: ${error.response?.data?.detail || 'Failed'}`); }
    }
    if (errors.length > 0) alert(`Some deletions failed:\n${errors.join('\n')}`);
    setSelectedIds([]);
    setShowBulkDeleteConfirm(false);
    setConfirmText('');
    fetchData();
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: 0, current_quantity: 0, adjusted_quantity: 0, difference: 0 }]
    });
  };

  const updateItem = async (index: number, field: string, value: any) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-fetch current quantity when product is selected
    if (field === 'product_id' && value && formData.warehouse_id) {
      try {
        const response = await api.get(`/inventory/stock/`, {
          params: {
            product_id: value,
            warehouse_id: formData.warehouse_id
          }
        });
        
        if (response.data && response.data.length > 0) {
          const stockRecord = response.data[0];
          newItems[index].current_quantity = stockRecord.quantity_on_hand || 0;
        } else {
          newItems[index].current_quantity = 0;
        }
      } catch (error) {
        console.warn('Could not fetch current quantity:', error);
        newItems[index].current_quantity = 0;
      }
    }
    
    // Auto-calculate difference when quantities change
    if (field === 'current_quantity' || field === 'adjusted_quantity') {
      newItems[index].difference = newItems[index].adjusted_quantity - newItems[index].current_quantity;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    // Sync validation values with form data
    setValidationValue('warehouse_id', formData.warehouse_id);
    setValidationValue('reason', formData.reason);
    setValidationValue('items', formData.items);
    
    // Validate form
    if (!validateForm()) {
      // Mark all fields as touched to show errors
      setFieldTouched('warehouse_id');
      setFieldTouched('reason');
      setFieldTouched('items');
      return;
    }

    if (!formData.warehouse_id || formData.items.length === 0) {
      alert('Please select a warehouse and add at least one item');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/inventory/adjustments/', formData);
      alert('Stock adjustment created successfully');
      setShowForm(false);
      setFormData({ warehouse_id: 0, reason: 'physical_count', notes: '', items: [] });
      resetValidation();
      fetchData();
    } catch (error) {
      console.error('Error creating adjustment:', error);
      alert('Failed to create adjustment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedAdjustments = [...adjustments].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = '', bVal: any = '';
    switch (sortField) {
      case 'adjustment_number': aVal = a.adjustment_number || ''; bVal = b.adjustment_number || ''; break;
      case 'adjustment_date': aVal = a.adjustment_date || ''; bVal = b.adjustment_date || ''; break;
      case 'warehouse': aVal = a.warehouse?.name || ''; bVal = b.warehouse?.name || ''; break;
      case 'reason': aVal = a.reason || ''; bVal = b.reason || ''; break;
      case 'items': aVal = a.items?.length || 0; bVal = b.items?.length || 0; break;
      case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
      default: return 0;
    }
    if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const [pendingStatusChanges, setPendingStatusChanges] = useState<Record<number, string>>({});
  const [viewingAdjustment, setViewingAdjustment] = useState<StockAdjustment | null>(null);

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await api.put(`/inventory/adjustments/${id}`, { status: newStatus });
      setPendingStatusChanges(prev => { const n = {...prev}; delete n[id]; return n; });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Bulk import handlers
  const handleBulkImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await api.post('/inventory/adjustments/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      fetchData();
    } catch (error: any) {
      setImportResult({ error: error.response?.data?.detail || 'Import failed' });
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/inventory/adjustments/bulk-import-template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'adjustment_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download template');
    }
  };

  // Bulk status update handler
  const handleBulkStatusUpdate = async () => {
    if (!bulkStatusTarget || selectedIds.length === 0) return;
    setBulkStatusLoading(true);
    try {
      const response = await api.post('/inventory/adjustments/bulk-status', {
        adjustment_ids: selectedIds,
        status: bulkStatusTarget
      });
      const data = response.data;
      if (data.errors && data.errors.length > 0) {
        alert(`Updated ${data.total_updated} adjustment(s).\n\nErrors:\n${data.errors.join('\n')}`);
      } else {
        alert(`Successfully updated ${data.total_updated} adjustment(s) to "${bulkStatusTarget}"`);
      }
      setSelectedIds([]);
      setShowBulkStatus(false);
      setBulkStatusTarget('');
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Bulk status update failed');
    } finally {
      setBulkStatusLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      draft: 'status-draft',
      approved: 'status-active',
      completed: 'status-active'
    };
    return (
      <span className={`status-badge ${statusColors[status] || 'status-draft'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 className="card-title">Stock Adjustments</h2>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: '200px' }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search adjustment #, warehouse, reason, status..."
            style={{ padding: '6px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', width: '100%', maxWidth: '400px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowBulkImport(true); setImportFile(null); setImportResult(null); }}>
            <span className="btn-icon">📥</span>
            Bulk Import
          </button>
          {/* TODO: "New Adjustment" button should only show for admin/manager roles (not staff).
              Currently we don't pass userRole to this component — will address in a future pass. */}
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
            <span className="btn-icon">➕</span>
            New Adjustment
          </button>
        </div>
      </div>

      <CollapsibleFilters label="Filters">
        <div style={{ padding: '10px 20px', borderBottom: '1px solid #f0f3f5', background: '#fafbfc', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <label style={{ fontSize: '12px', color: '#64748b' }}>From:</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '12px' }} />
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <label style={{ fontSize: '12px', color: '#64748b' }}>To:</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '12px' }} />
          </div>
          <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>
        </div>
      </CollapsibleFilters>

      {selectedIds.length > 0 && (
        <div style={{ padding: '1rem 2rem', background: '#fef3c7', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>{selectedIds.length} adjustments selected</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowBulkStatus(true)}>
            Update Status
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleBulkDeleteClick}>
            Delete Selected
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '50px' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '15%' }} />
          </colgroup>
          <thead>
            <tr>
              <th><input type="checkbox" className="checkbox" checked={selectedIds.length === adjustments.length && adjustments.length > 0} onChange={(e) => handleSelectAll(e.target.checked)} /></th>
              <th onClick={() => handleSort('adjustment_number')} style={{ cursor: 'pointer' }}>Adjustment # {sortField === 'adjustment_number' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('adjustment_date')} style={{ cursor: 'pointer' }}>Date {sortField === 'adjustment_date' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('warehouse')} style={{ cursor: 'pointer' }}>Warehouse {sortField === 'warehouse' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('reason')} style={{ cursor: 'pointer' }}>Reason {sortField === 'reason' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('items')} style={{ cursor: 'pointer' }}>Items {sortField === 'items' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {adjustments.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  No adjustments found. Create your first stock adjustment.
                </td>
              </tr>
            ) : (
              sortedAdjustments.map(adj => (
                <tr key={adj.id} onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('select') || target.closest('button') || target.closest('input[type="checkbox"]')) return;
                  setViewingAdjustment(adj);
                }} style={{ cursor: 'pointer' }}>
                  <td><input type="checkbox" className="checkbox" checked={selectedIds.includes(adj.id)} onChange={(e) => handleSelectItem(adj.id, e.target.checked)} /></td>
                  <td style={{ wordBreak: 'break-word' }}>{adj.adjustment_number}</td>
                  <td>{new Date(adj.adjustment_date).toLocaleDateString()}</td>
                  <td style={{ wordBreak: 'break-word' }}>{adj.warehouse?.name || '-'}</td>
                  <td style={{ wordBreak: 'break-word' }}>{adj.reason.replace('_', ' ')}</td>
                  <td>{adj.items?.length || 0} items</td>
                  <td>
                    <select
                      value={pendingStatusChanges[adj.id] ?? adj.status}
                      onChange={(e) => setPendingStatusChanges(prev => ({...prev, [adj.id]: e.target.value}))}
                      className="form-select"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="draft">Draft</option>
                      <option value="approved">Approved</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {pendingStatusChanges[adj.id] && pendingStatusChanges[adj.id] !== adj.status && (
                        <button
                          className="btn btn-primary"
                          style={{ fontSize: '10px', padding: '3px 8px', whiteSpace: 'nowrap' }}
                          onClick={() => handleStatusChange(adj.id, pendingStatusChanges[adj.id])}
                        >
                          Submit
                        </button>
                      )}
                      <button className="btn btn-danger" onClick={() => handleDelete(adj.id)} style={{ fontSize: '12px', padding: '4px 8px' }}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {adjustments.length > 0 && (
        <div style={{ 
          padding: '1rem 2rem', 
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'white'
        }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} adjustments
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', color: '#64748b', marginRight: '0.5rem' }}>
              Per page:
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                fontSize: '14px'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>

            <span style={{ fontSize: '14px', color: '#64748b', padding: '0 0.5rem' }}>
              Page {currentPage} of {Math.ceil(totalCount / pageSize)}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / pageSize), prev + 1))}
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage >= Math.ceil(totalCount / pageSize) ? 0.5 : 1,
                cursor: currentPage >= Math.ceil(totalCount / pageSize) ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Bulk Delete</h3>
            <p>You are about to delete {selectedIds.length} stock adjustments. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input type="text" className="form-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type confirm" />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button className="btn btn-danger" onClick={confirmBulkDelete} disabled={confirmText !== 'confirm'}>Delete Adjustments</button>
              <button className="btn btn-cancel" onClick={() => { setShowBulkDeleteConfirm(false); setConfirmText(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className={`modal ${isSubmitting ? 'form-loading' : ''}`} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto', padding: '32px' }}>
            <FormSection 
              title="Create Stock Adjustment"
              subtitle="Adjust inventory quantities based on physical counts or other reasons"
            >
              <ValidationSummary 
                errors={validationErrors}
                title="Please correct the following errors before submitting:"
              />

              <FormGrid columns={2}>
                <EnhancedFormField 
                  label="Warehouse" 
                  required 
                  tooltip="Select the warehouse where the adjustment will be applied"
                  error={validationErrors.warehouse_id}
                  touched={validationTouched.warehouse_id}
                  onBlur={() => setFieldTouched('warehouse_id')}
                  helpText="Choose the warehouse location for this adjustment"
                >
                  <StandardSelect
                    value={formData.warehouse_id}
                    onChange={async (e) => {
                      const warehouseId = parseInt(e.target.value);
                      setFormData({ ...formData, warehouse_id: warehouseId });
                      setValidationValue('warehouse_id', warehouseId);
                      
                      // Refresh current quantities for existing items when warehouse changes
                      if (warehouseId && formData.items.length > 0) {
                        const updatedItems = await Promise.all(
                          formData.items.map(async (item) => {
                            if (item.product_id) {
                              try {
                                const response = await api.get(`/inventory/stock/`, {
                                  params: {
                                    product_id: item.product_id,
                                    warehouse_id: warehouseId
                                  }
                                });
                                
                                if (response.data && response.data.length > 0) {
                                  const stockRecord = response.data[0];
                                  return {
                                    ...item,
                                    current_quantity: stockRecord.quantity_on_hand || 0,
                                    difference: item.adjusted_quantity - (stockRecord.quantity_on_hand || 0)
                                  };
                                }
                              } catch (error) {
                                console.warn('Could not fetch current quantity:', error);
                              }
                            }
                            return { ...item, current_quantity: 0, difference: item.adjusted_quantity };
                          })
                        );
                        setFormData(prev => ({ ...prev, items: updatedItems }));
                      }
                    }}
                    error={!!validationErrors.warehouse_id}
                  >
                    <option value={0}>Select Warehouse</option>
                    {warehouses.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </StandardSelect>
                </EnhancedFormField>

                <EnhancedFormField 
                  label="Reason" 
                  required
                  tooltip="Select the reason for this stock adjustment"
                  error={validationErrors.reason}
                  touched={validationTouched.reason}
                  onBlur={() => setFieldTouched('reason')}
                  helpText="Choose the most appropriate reason for this adjustment"
                >
                  <ReasonSelector
                    value={formData.reason}
                    onChange={(value) => {
                      setFormData({ ...formData, reason: value });
                      setValidationValue('reason', value);
                    }}
                    reasons={ADJUSTMENT_REASONS}
                    placeholder="Select adjustment reason..."
                    required
                    error={!!validationErrors.reason}
                  />
                </EnhancedFormField>
              </FormGrid>

              <EnhancedFormField 
                label="Notes"
                tooltip="Additional details about this adjustment (optional)"
                helpText="Provide any additional context or details about this adjustment"
              >
                <StandardTextarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Enter any additional notes about this adjustment..."
                />
              </EnhancedFormField>

              <div style={{ marginTop: '1.5rem' }}>
                <EnhancedFormField
                  label="Adjustment Items"
                  required
                  error={validationErrors.items}
                  touched={validationTouched.items}
                  onBlur={() => setFieldTouched('items')}
                  helpText="Add products and specify their adjusted quantities"
                >
                  <ItemList
                    items={formData.items}
                    onAddItem={addItem}
                    onRemoveItem={removeItem}
                    onUpdateItem={updateItem}
                    addButtonText="Add Product"
                    emptyMessage="No products added yet. Click 'Add Product' to start."
                    renderItem={(item, index, onUpdate, onRemove) => (
                      <div className="item-grid item-grid-5" style={{ gap: '10px', alignItems: 'flex-end' }}>
                        <FormField label="Product" required>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              placeholder="Type SKU or product name..."
                              defaultValue={item.product_id ? (products.find(p => p.id === item.product_id) ? getProductLabel(products.find(p => p.id === item.product_id)!) : '') : ''}
                              onChange={(e) => {
                                const val = e.target.value.toLowerCase();
                                const match = products.find(p => p.name.toLowerCase() === val || p.sku.toLowerCase() === val || getProductLabel(p).toLowerCase() === val);
                                if (match) onUpdate('product_id', match.id);
                                else if (!e.target.value) onUpdate('product_id', 0);
                              }}
                              list={`adj-products-list-${index}`}
                              style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
                            />
                            <datalist id={`adj-products-list-${index}`}>
                              {products.map(p => (
                                <option key={p.id} value={getProductLabel(p)}>{getProductLabel(p)}</option>
                              ))}
                            </datalist>
                          </div>
                        </FormField>

                        <QuantityInput
                          label="Current Qty"
                          value={item.current_quantity}
                          onChange={(value) => onUpdate('current_quantity', value)}
                          tooltip="Current quantity in system"
                          disabled={true}
                        />

                        <QuantityInput
                          label="New Qty"
                          value={item.adjusted_quantity}
                          onChange={(value) => onUpdate('adjusted_quantity', value)}
                          tooltip="New quantity after adjustment"
                          placeholder="New qty"
                        />

                        <DifferenceDisplay
                          current={item.current_quantity}
                          adjusted={item.adjusted_quantity}
                          label="Diff"
                          showPercentage={true}
                        />

                        <button 
                          type="button"
                          className="item-remove-btn"
                          onClick={onRemove}
                          title="Remove this item"
                          style={{ alignSelf: 'center', marginTop: '20px' }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  />
                </EnhancedFormField>
              </div>

              <FormActions>
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowForm(false);
                    setFormData({ warehouse_id: 0, reason: 'physical_count', notes: '', items: [] });
                    resetValidation();
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn btn-primary" 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Adjustment'}
                </button>
              </FormActions>
            </FormSection>
          </div>
        </div>
      )}

      {viewingAdjustment && (
        <div className="modal-overlay" onClick={() => setViewingAdjustment(null)}>
          <div className="modal" style={{ maxWidth: '700px', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setViewingAdjustment(null)}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}
              aria-label="Close"
            >×</button>
            <h3 style={{ marginBottom: '1.25rem' }}>Stock Adjustment Details</h3>
            <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '1.25rem', border: '1px solid #e2e8f0' }}>
              <div className="form-grid-2" style={{ gap: '1rem', marginBottom: 0 }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Adjustment #</div>
                  <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{viewingAdjustment.adjustment_number}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Status</div>
                  <div>{getStatusBadge(viewingAdjustment.status)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Warehouse</div>
                  <div style={{ fontWeight: 500 }}>{viewingAdjustment.warehouse?.name || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Date</div>
                  <div style={{ fontWeight: 500 }}>{new Date(viewingAdjustment.adjustment_date).toLocaleDateString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Reason</div>
                  <div style={{ fontWeight: 500 }}>{viewingAdjustment.reason.replace(/_/g, ' ')}</div>
                </div>
                {viewingAdjustment.notes && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '2px' }}>Notes</div>
                    <div style={{ fontWeight: 500 }}>{viewingAdjustment.notes}</div>
                  </div>
                )}
              </div>
            </div>

            <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '14px' }}>Items</h4>
            <table className="table" style={{ fontSize: '13px' }}>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Current Qty</th>
                  <th>Adjusted Qty</th>
                  <th>Difference</th>
                </tr>
              </thead>
              <tbody>
                {viewingAdjustment.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td>{products.find(p => p.id === item.product_id)?.name || `Product ${item.product_id}`}</td>
                    <td>{item.current_quantity}</td>
                    <td>{item.adjusted_quantity}</td>
                    <td style={{ color: item.difference > 0 ? '#16a34a' : item.difference < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                      {item.difference > 0 ? '+' : ''}{item.difference}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
              <button className="btn btn-secondary" onClick={() => setViewingAdjustment(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="modal-overlay" onClick={() => { if (!importLoading) setShowBulkImport(false); }}>
          <div className="modal" style={{ maxWidth: '520px', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => { if (!importLoading) setShowBulkImport(false); }}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}
              aria-label="Close"
            >×</button>
            <h3 style={{ marginBottom: '0.5rem' }}>Bulk Import Adjustments</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '1.25rem' }}>
              Upload a CSV file with columns: <strong>SKU</strong> (required), <strong>Qty</strong> (required), <strong>Warehouse</strong> (optional), <strong>Reason</strong> (optional).
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleDownloadTemplate}
                style={{ fontSize: '12px' }}
              >
                📄 Download Template
              </button>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Select CSV File
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                style={{ fontSize: '13px' }}
              />
            </div>

            {importResult && !importResult.error && (
              <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#d4edda', border: '1px solid #c3e6cb', marginBottom: '1rem' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#155724', marginBottom: '4px' }}>
                  {importResult.message}
                </div>
                {importResult.adjustments?.map((adj: any, i: number) => (
                  <div key={i} style={{ fontSize: '12px', color: '#155724' }}>
                    {adj.adjustment_number} — {adj.warehouse} — {adj.reason} ({adj.item_count} items)
                  </div>
                ))}
                {importResult.errors?.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#856404' }}>
                    <strong>Warnings ({importResult.total_errors}):</strong>
                    {importResult.errors.map((err: string, i: number) => (
                      <div key={i}>{err}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {importResult?.error && (
              <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f8d7da', border: '1px solid #f5c6cb', marginBottom: '1rem' }}>
                <div style={{ fontSize: '13px', color: '#721c24' }}>{importResult.error}</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowBulkImport(false)} disabled={importLoading}>
                {importResult && !importResult.error ? 'Done' : 'Cancel'}
              </button>
              {(!importResult || importResult.error) && (
                <button
                  className="btn btn-primary"
                  onClick={handleBulkImport}
                  disabled={!importFile || importLoading}
                >
                  {importLoading ? 'Importing...' : 'Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      {showBulkStatus && (
        <div className="modal-overlay" onClick={() => { if (!bulkStatusLoading) setShowBulkStatus(false); }}>
          <div className="modal" style={{ maxWidth: '400px', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => { if (!bulkStatusLoading) setShowBulkStatus(false); }}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}
              aria-label="Close"
            >×</button>
            <h3 style={{ marginBottom: '0.5rem' }}>Bulk Status Update</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '1.25rem' }}>
              Update status for {selectedIds.length} selected adjustment(s).
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                New Status
              </label>
              <select
                value={bulkStatusTarget}
                onChange={(e) => setBulkStatusTarget(e.target.value)}
                className="form-select"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
              >
                <option value="">Select status...</option>
                <option value="approved">Approved</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {bulkStatusTarget === 'completed' && (
              <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#fff3cd', border: '1px solid #ffc107', fontSize: '12px', color: '#856404', marginBottom: '1rem' }}>
                Completing adjustments will apply inventory changes immediately.
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setShowBulkStatus(false); setBulkStatusTarget(''); }} disabled={bulkStatusLoading}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBulkStatusUpdate}
                disabled={!bulkStatusTarget || bulkStatusLoading}
              >
                {bulkStatusLoading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Adjustment"
        message="Are you sure you want to delete this stock adjustment? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default StockAdjustmentManager;
